import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Leaf, BellRing, KanbanSquare, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { errMsg } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Logo } from "../components/Logo";

const FEATURES = [
  { icon: KanbanSquare, t: "Visual lead pipeline", d: "Drag deals from New to Won in seconds" },
  { icon: BellRing, t: "5-stage follow-ups", d: "Never miss a call, meeting or demo again" },
  { icon: ShieldCheck, t: "Role-based access", d: "Admin, Sales, HR & Accounts workspaces" },
];

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (user) nav("/", { replace: true }); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}`);
      nav("/");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex flex-col justify-between bg-emerald-700 p-12 text-white relative overflow-hidden">
        <Leaf className="absolute -right-24 -bottom-24 h-[28rem] w-[28rem] text-emerald-600/40" strokeWidth={1} />
        <div className="flex items-center gap-3 relative">
          <img src="/favicon.svg" alt="" className="h-11 w-11 rounded-xl ring-2 ring-white/30" />
          <span className="text-xl font-extrabold tracking-tight">GREENRGY CRM</span>
        </div>
        <div className="relative max-w-md">
          <h1 className="text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">Turn every lead into lasting energy.</h1>
          <p className="mt-4 text-emerald-100">One workspace for your sales, HR and accounts teams to capture, nurture and close business.</p>
          <div className="mt-10 space-y-5">
            {FEATURES.map(({ icon: I, t, d }) => (
              <div key={t} className="flex gap-4">
                <div className="rounded-lg bg-white/10 p-2.5 h-fit"><I className="h-5 w-5" /></div>
                <div><div className="font-semibold">{t}</div><div className="text-sm text-emerald-100">{d}</div></div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-emerald-200">© {new Date().getFullYear()} GREENRGY. All rights reserved.</div>
      </div>
      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6 fade-up" data-testid="login-form">
          <div className="lg:hidden"><Logo /></div>
          <div>
            <h2 className="text-2xl font-extrabold tracking-tight">Sign in to your workspace</h2>
            <p className="mt-1 text-sm text-slate-500">Use the credentials provided by your administrator.</p>
          </div>
          <div className="space-y-4">
            <Input data-testid="login-email-input" type="email" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-11" />
            <Input data-testid="login-password-input" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required className="h-11" />
          </div>
          <div className="text-right -mt-3">
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm font-semibold text-emerald-700 hover:text-emerald-800">Forgot password?</Link>
          </div>
          {error && <div data-testid="login-error" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}
          <Button data-testid="login-submit-btn" type="submit" disabled={busy} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 rounded-lg font-semibold active:scale-[0.98] transition-transform">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}
